import { useState } from 'react'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Database from '../../types/Database'
import * as joinRequestService from '../../services/joinRequestService'

export type PendingRequestCardProps = {
  request: Database.PendingRequestView
  isAtCapacity: boolean
  onActionComplete?: () => void
}

type ActionInProgress = 'approve' | 'decline' | 'alwaysApprove' | null

const STATUS_CHIP_CONFIG: Record<
  Database.JoinRequestStatus,
  { label: string; color: 'default' | 'success' | 'warning' | 'error' | 'info' }
> = {
  pending: { label: 'Pending', color: 'warning' },
  approved: { label: 'Approved', color: 'success' },
  declined: { label: 'Declined', color: 'error' },
  forfeited: { label: 'Forfeited', color: 'default' },
  cancelled: { label: 'Cancelled', color: 'default' }
}

const PAYMENT_CHIP_CONFIG: Record<
  NonNullable<Database.JoinRequestPaymentStatus>,
  { label: string; color: 'default' | 'success' | 'warning' | 'error' | 'info' }
> = {
  awaiting: { label: 'Payment Pending', color: 'warning' },
  paid: { label: 'Paid', color: 'success' },
  expired: { label: 'Payment Expired', color: 'error' }
}

const PendingRequestCard = ({
  request,
  isAtCapacity,
  onActionComplete
}: PendingRequestCardProps) => {
  const [actionInProgress, setActionInProgress] =
    useState<ActionInProgress>(null)
  const [error, setError] = useState<string | null>(null)

  const isProfileUnavailable = !request.displayName
  const isPending = request.status === 'pending'
  const isApprovedUnpaid =
    request.status === 'approved' && request.paymentStatus !== 'paid'
  const isDeclined = request.status === 'declined'
  const canApprove = isPending
  const canDecline = isPending || isApprovedUnpaid
  const canAlwaysApprove = isPending

  const formatDistance = (distanceKm: number | null): string => {
    if (distanceKm == null) return '\u2014'
    return `${Math.round(distanceKm)} km`
  }

  const handleApprove = async () => {
    setActionInProgress('approve')
    setError(null)
    try {
      await joinRequestService.approveRequest(request.requestId)
      onActionComplete?.()
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to approve request'
      setError(message)
    } finally {
      setActionInProgress(null)
    }
  }

  const handleDecline = async () => {
    setActionInProgress('decline')
    setError(null)
    try {
      await joinRequestService.declineRequest(request.requestId)
      onActionComplete?.()
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to decline request'
      setError(message)
    } finally {
      setActionInProgress(null)
    }
  }

  const handleAlwaysApprove = async () => {
    setActionInProgress('alwaysApprove')
    setError(null)
    try {
      await joinRequestService.alwaysApprove(request.requestId)
      onActionComplete?.()
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to always approve request'
      setError(message)
    } finally {
      setActionInProgress(null)
    }
  }

  const statusChip = STATUS_CHIP_CONFIG[request.status]
  const paymentChip = request.paymentStatus
    ? PAYMENT_CHIP_CONFIG[request.paymentStatus]
    : null

  if (isProfileUnavailable) {
    return (
      <Card variant='outlined' sx={{ opacity: isDeclined ? 0.6 : 1 }}>
        <CardContent>
          <Typography variant='body2' color='text.secondary'>
            Profile unavailable
          </Typography>
          <Stack direction='row' spacing={1} sx={{ mt: 1 }}>
            <Chip
              label={statusChip.label}
              size='small'
              color={statusChip.color}
            />
            {paymentChip && (
              <Chip
                label={paymentChip.label}
                size='small'
                color={paymentChip.color}
              />
            )}
          </Stack>
          {canDecline && (
            <Stack direction='row' spacing={1} sx={{ mt: 2 }}>
              <Button
                variant='outlined'
                color='error'
                size='small'
                onClick={handleDecline}
                disabled={actionInProgress !== null}
                startIcon={
                  actionInProgress === 'decline' ? (
                    <CircularProgress size={16} />
                  ) : undefined
                }
              >
                {isApprovedUnpaid ? 'Reject' : 'Decline'}
              </Button>
            </Stack>
          )}
          {error && (
            <Alert severity='error' sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}
        </CardContent>
      </Card>
    )
  }

  return (
    <Card variant='outlined' sx={{ opacity: isDeclined ? 0.6 : 1 }}>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 1
          }}
        >
          <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
            {request.displayName}
          </Typography>
          <Chip
            label={request.accreditationStatus}
            size='small'
            color={
              request.accreditationStatus === 'verified' ? 'success' : 'default'
            }
          />
        </Box>

        <Stack direction='row' spacing={1} sx={{ mb: 1 }}>
          <Chip
            label={statusChip.label}
            size='small'
            color={statusChip.color}
            variant='outlined'
          />
          {paymentChip && (
            <Chip
              label={paymentChip.label}
              size='small'
              color={paymentChip.color}
              variant='outlined'
            />
          )}
        </Stack>

        <Stack direction='row' spacing={2} sx={{ mb: 2 }}>
          <Typography variant='body2' color='text.secondary'>
            {formatDistance(request.distanceKm)}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {request.pastDivesTogether}{' '}
            {request.pastDivesTogether === 1 ? 'dive' : 'dives'} together
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {request.totalDivesCompleted} total{' '}
            {request.totalDivesCompleted === 1 ? 'dive' : 'dives'}
          </Typography>
        </Stack>

        <Stack direction='row' spacing={1}>
          {canApprove && (
            <Button
              variant='contained'
              size='small'
              onClick={handleApprove}
              disabled={isAtCapacity || actionInProgress !== null}
              startIcon={
                actionInProgress === 'approve' ? (
                  <CircularProgress size={16} />
                ) : undefined
              }
            >
              Approve
            </Button>
          )}
          {canDecline && (
            <Button
              variant='outlined'
              color='error'
              size='small'
              onClick={handleDecline}
              disabled={actionInProgress !== null}
              startIcon={
                actionInProgress === 'decline' ? (
                  <CircularProgress size={16} />
                ) : undefined
              }
            >
              {isApprovedUnpaid ? 'Reject' : 'Decline'}
            </Button>
          )}
          {canAlwaysApprove && (
            <Button
              variant='outlined'
              size='small'
              onClick={handleAlwaysApprove}
              disabled={isAtCapacity || actionInProgress !== null}
              startIcon={
                actionInProgress === 'alwaysApprove' ? (
                  <CircularProgress size={16} />
                ) : undefined
              }
            >
              Always Approve
            </Button>
          )}
        </Stack>

        {error && (
          <Alert severity='error' sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
      </CardContent>
    </Card>
  )
}

export default PendingRequestCard
